import type { AuthState, User } from "$lib/types";

export class AuthError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "AuthError";
  }
}

export class AuthAPI {
  private static readonly API_URL = "/api/v1/auth";
  private static readonly ENDPOINTS = {
    status: "/status",
    user: "/user",
    signin: "/signin",
    signup: "/signup",
    signout: "/signout",
    permission: "/permission_check",
    googleOAuth: "/google_oauth2",
    passwordReset: "/password_reset",
    checkPasswordReset: "/password_reset/check",
    submitPasswordReset: "/password_reset/submit",
  } as const;

  private static readonly HEADERS = {
    contentType: "Content-Type",
    jsonContentType: "application/json",
    formContentType: "application/x-www-form-urlencoded",
  } as const;

  /**
   * Handles standard server responses, parsing JSON and checking for errors
   */
  private static async handleServerResponse(response: Response) {
    const contentType = response.headers.get("content-type");
    let data;

    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      throw new AuthError("Unexpected server response", response.status);
    }

    if (!response.ok) {
      let errorMessage = "An unexpected error occurred";
      const serverMessage = typeof data.message === "object" ? data.message.message : data.message;

      switch (response.status) {
        case 400:
          errorMessage = serverMessage || "Bad request";
          break;
        case 401:
          errorMessage = serverMessage || "Invalid email or password";
          break;
        case 403:
          errorMessage = serverMessage || "You do not have permission to perform this action.";
          break;
        case 404:
          errorMessage = serverMessage || "Resource not found";
          break;
        case 422:
          errorMessage = serverMessage || "Validation failed. Please check your input.";
          break;
        case 500:
          errorMessage = serverMessage || "Server error. Please try again later.";
          break;
        default:
          errorMessage = serverMessage || `Server error: ${response.status}`;
      }

      throw new AuthError(errorMessage, response.status);
    }

    return data;
  }

  /**
   * Checks the user's authentication status
   */
  static async checkStatus(): Promise<boolean | AuthError> {
    try {
      const response = await fetch(`${this.API_URL}${this.ENDPOINTS.status}`, {
        credentials: "include",
      });

      const data = await this.handleServerResponse(response);
      return data.success;
    } catch (error) {
      if (error instanceof AuthError) {
        return error;
      }
      return new AuthError(
        `Failed to check auth status: ${error instanceof Error ? error.message : "Unknown error"}`,
        500,
      );
    }
  }

  /**
   * Fetches the current user's data
   */
  static async fetchUser(): Promise<User | AuthError> {
    try {
      const response = await fetch(`${this.API_URL}${this.ENDPOINTS.user}`, {
        method: "GET",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        credentials: "include",
      });

      const data = await this.handleServerResponse(response);
      return data;
    } catch (error) {
      if (error instanceof AuthError) {
        return error;
      }
      return new AuthError(`Failed to fetch user: ${error instanceof Error ? error.message : "Unknown error"}`, 500);
    }
  }

  /**
   * Checks if the user has admin permissions
   * @param customFetch Optional fetch function for server-side usage
   */
  static async checkAdminPermissions(customFetch?: typeof fetch): Promise<boolean> {
    try {
      const fetchFn = customFetch || fetch;

      const response = await fetchFn(`${this.API_URL}${this.ENDPOINTS.permission}`, {
        credentials: "include",
      });

      return response.ok;
    } catch (error) {
      console.error("Error checking admin permissions:", error);
      return false;
    }
  }

  /**
   * Signs in a user with email and password
   */
  static async signin(email: string, password: string): Promise<AuthState | AuthError> {
    try {
      const response = await fetch(`${this.API_URL}${this.ENDPOINTS.signin}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });

      const data = await this.handleServerResponse(response);

      if (data.success) {
        const userData = await this.fetchUser();
        if (userData instanceof AuthError) {
          throw userData;
        }

        return {
          isLoggedIn: true,
          user: userData,
        };
      }

      return { isLoggedIn: false, user: undefined };
    } catch (error) {
      if (error instanceof AuthError) {
        return error;
      }
      return new AuthError(`Failed to sign in: ${error instanceof Error ? error.message : "Unknown error"}`, 500);
    }
  }

  /**
   * Signs up a new user
   */
  static async signup(
    email: string,
    password: string,
    confirmedPassword: string,
    referralCode: string = "",
  ): Promise<AuthState | AuthError> {
    try {
      const payload = {
        email,
        password,
        confirmedPassword,
        referralCode,
      };

      const response = await fetch(`${this.API_URL}${this.ENDPOINTS.signup}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      const data = await this.handleServerResponse(response);

      if (data.success) {
        const userData = await this.fetchUser();
        if (userData instanceof AuthError) {
          throw userData;
        }

        return {
          isLoggedIn: true,
          user: userData,
        };
      }

      return { isLoggedIn: false, user: undefined };
    } catch (error) {
      if (error instanceof AuthError) {
        return error;
      }
      return new AuthError(`Failed to sign up: ${error instanceof Error ? error.message : "Unknown error"}`, 500);
    }
  }

  /**
   * Signs out the current user
   */
  static async signout(): Promise<boolean | AuthError> {
    try {
      const response = await fetch(`${this.API_URL}${this.ENDPOINTS.signout}`, {
        method: "POST",
        credentials: "include",
      });

      const data = await this.handleServerResponse(response);
      return data.success;
    } catch (error) {
      if (error instanceof AuthError) {
        return error;
      }
      return new AuthError(`Failed to sign out: ${error instanceof Error ? error.message : "Unknown error"}`, 500);
    }
  }

  /**
   * Initiates Google sign-in
   */
  static initiateGoogleSignin(): void {
    if (typeof window !== "undefined") {
      localStorage.setItem("pendingGoogleAuth", "true");
      window.location.href = `${this.API_URL}${this.ENDPOINTS.googleOAuth}`;
    }
  }

  /**
   * Requests a password reset for a user
   */
  static async requestPasswordReset(email: string): Promise<boolean | AuthError> {
    try {
      const response = await fetch(`${this.API_URL}${this.ENDPOINTS.passwordReset}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.formContentType,
        },
        body: new URLSearchParams({ email }),
      });

      const data = await this.handleServerResponse(response);
      return data.success;
    } catch (error) {
      if (error instanceof AuthError) {
        return error;
      }
      return new AuthError(
        `Failed to request password reset: ${error instanceof Error ? error.message : "Unknown error"}`,
        500,
      );
    }
  }

  /**
   * Checks if a password reset token is valid
   */
  static async checkPasswordResetToken(token: string): Promise<boolean | AuthError> {
    try {
      const response = await fetch(
        `${this.API_URL}${this.ENDPOINTS.checkPasswordReset}?token=${encodeURIComponent(token)}`,
        {
          method: "GET",
          headers: {
            [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
          },
        },
      );

      const data = await this.handleServerResponse(response);
      return data.success;
    } catch (error) {
      if (error instanceof AuthError) {
        return error;
      }
      return new AuthError(
        `Failed to check password reset token: ${error instanceof Error ? error.message : "Unknown error"}`,
        500,
      );
    }
  }

  /**
   * Submits a new password for a reset
   */
  static async submitPasswordReset(
    token: string,
    password: string,
    confirmedPassword: string,
  ): Promise<boolean | AuthError> {
    try {
      const response = await fetch(`${this.API_URL}${this.ENDPOINTS.submitPasswordReset}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.formContentType,
        },
        body: new URLSearchParams({
          token,
          password,
          confirmedPassword,
        }),
      });

      const data = await this.handleServerResponse(response);
      return data.success;
    } catch (error) {
      if (error instanceof AuthError) {
        return error;
      }
      return new AuthError(
        `Failed to submit password reset: ${error instanceof Error ? error.message : "Unknown error"}`,
        500,
      );
    }
  }
}
